import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { filter } from 'rxjs/operators';
import { DuctProperties, SubductProperties, UndergroundProperties } from '../../state/edge.model';

@Component({
  selector: 'app-edge-duct-form',
  templateUrl: './edge-duct-form.component.html',
  styleUrls: ['./edge-duct-form.component.scss']
})
export class EdgDuctFormComponent implements OnInit {

  @Input()
  edgeForm: FormGroup;

  @Input()
  properties: UndergroundProperties;

  @Output()
  propertiesChange = new EventEmitter<UndergroundProperties>();

  baseHeightForm: FormGroup;

  form: FormGroup;

  constructor(
    private fb: FormBuilder
  ) { }

  get ductArrayForms() {
    return this.form.controls.ducts as FormArray;
  }

  ngOnInit(): void {
    const { ducts } = this.properties;
    const base = ducts.length;
    const height = ducts[0] && ducts[0].length > 0 ? ducts[0].length : null;
    const installationBase = this.properties.installation_base ? this.properties.installation_base : 'soil';

    this.baseHeightForm = this.fb.group({
      base: [base, { updateOn: 'blur', validators: [Validators.required, Validators.min(1), Validators.max(16)] }],
      height: [height, { updateOn: 'blur', validators: [Validators.required, Validators.min(1), Validators.max(8)] }],
    });

    this.form = this.fb.group({
      installation_base: [installationBase, Validators.required],
      duct_diameter: [this.properties.duct_diameter, [Validators.required, Validators.min(1)]],
      subduct_diameter: [this.properties.subduct_diameter, [Validators.required, Validators.min(1)]],
      ducts: this.createDuctArrayForms(ducts)
    });

    this.baseHeightForm.valueChanges
      .pipe(filter(() => this.baseHeightForm.valid))
      .subscribe(value => this.changeBaseAndHeight(value));

    this.form.valueChanges
      .pipe(filter(() => this.form.valid))
      .subscribe(value => this.updateProperties(value));


    if (this.edgeForm.get('properties')) {
      this.edgeForm.removeControl('properties');
    }

    this.edgeForm.addControl('properties', this.form);
  }

  getDuctPanelDescription(ductForm: FormControl) {
    const { value } = ductForm;

    if (value.has_subducts) {
      const occupieds = value.subducts.reduce((sum, subduct) => sum + subduct.occupied, 0);
      return `${occupieds}/${value.subducts.length}`;
    }
    return value.occupied ? 'occupied' : 'free';
  }

  getSubductForms(ductForm: FormControl): FormGroup[] {
    const formArray = ductForm.get('subducts') as FormArray;
    return formArray.controls as FormGroup[];
  }

  changeHasSubducts(ductForm: FormControl, hasSubducts: number): void {
    const formArray = ductForm.get('subducts') as FormArray;

    if (!hasSubducts) {
      while (formArray.length > 0) {
        formArray.removeAt(0);
      }
      formArray.clearValidators();
    } else {
      formArray.setValidators(Validators.required);
      ductForm.patchValue({ occupied: false });
    }
    formArray.updateValueAndValidity();
  }

  changeSubducts(ductForm: FormControl, quantity: number): void {
    const formArray = ductForm.get('subducts') as FormArray;
    this.reziseFormArray(formArray, quantity, () => this.createSubductForm({ occupied: false }));
  }

  private createDuctArrayForms(ductsArray: DuctProperties[][]): FormArray {
    const baseForm = ductsArray.map(ducts => this.createDuctForms(ducts));
    return this.fb.array(baseForm);
  }

  private createDuctForms(ducts: DuctProperties[]): FormArray {
    const forms = ducts.map(duct => this.createDuctForm(duct));
    return this.fb.array(forms);
  }

  private createDuctForm(duct: DuctProperties): FormGroup {
    const form = this.fb.group({
      occupied: [duct.occupied, [Validators.required]],
      has_subducts: [duct.subducts?.length > 0, [Validators.required]],
      subducts: this.createSubductForms(duct.subducts)
    });
    return form;
  }

  private createSubductForms(subducts: SubductProperties[]): FormArray {
    if (!subducts) {
      return this.fb.array([]);
    }
    const forms = subducts.map(subduct => this.createSubductForm(subduct));
    return this.fb.array(forms, [Validators.required]);
  }

  private createSubductForm(subduct: SubductProperties): FormGroup {
    return this.fb.group({
      occupied: [subduct.occupied, [Validators.required]],
    });
  }

  private updateProperties(formValue: any): void {
    const properties: UndergroundProperties = {
      duct_diameter: formValue.duct_diameter,
      subduct_diameter: formValue.subduct_diameter,
      installation_base: formValue.installation_base,
      ducts: formValue.ducts.map(ductArray => {
        return ductArray.map(ducValue => {
          const duct: DuctProperties = { occupied: ducValue.occupied };
          if (ducValue.has_subducts) {
            duct.subducts = ducValue.subducts.map(subducValue => {
              return { occupied: subducValue.occupied } as SubductProperties;
            });
          }
          return duct;
        });
      })
    };

    this.propertiesChange.emit(properties);
  }

  private changeBaseAndHeight(value: any) {
    const ductForms = this.form.get('ducts') as FormArray;

    if (this.baseHasChanged(value.base)) {
      this.changeBases(value.base, ductForms);
    }

    if (this.heigthHasChanged(value.height)) {
      this.changeHeights(value.height, ductForms);
    }
  }

  private changeBases(base: number, ductForms: FormArray) {
    const ducts = Array<DuctProperties>(base).fill({ occupied: false });

    this.reziseFormArray(
      ductForms,
      base,
      () => this.createDuctForms(ducts)
    );
  }

  private changeHeights(height: number, ductForms: FormArray) {
    ductForms.controls.forEach(baseForm => {
      this.reziseFormArray(
        baseForm as FormArray,
        height,
        () => this.createDuctForm({ occupied: false })
      );
    });
  }

  private baseHasChanged(base: number): boolean {
    const ductForms = this.form.get('ducts') as FormArray;
    return base !== ductForms.length;
  }

  private heigthHasChanged(height: number): boolean {
    const ductForms = this.form.get('ducts') as FormArray;
    return ductForms.value.some(duct => height !== duct[0]?.length);
  }

  private reziseFormArray(form: FormArray, size: number, cb: () => AbstractControl): void {
    if (size > form.length) {
      while (form.length < size) {
        form.push(cb());
      }
    } else {
      while (form.length > size) {
        form.removeAt(form.length - 1);
      }
    }
  }

}
